import { useState } from "react";
import { Headset } from "lucide-react";

import { SupportRequestModalContext } from "@/app/entities/SupportRequest";
import { useAuth } from "@/app/hooks/useAuth";
import { cn } from "@/app/utils/cn";
import { SupportRequestModal } from "@/view/components/SupportRequestModal";

type SupportRequestButtonVariant = "icon" | "floating" | "text" | "sidebar";

interface SupportRequestButtonProps {
  context?: SupportRequestModalContext;
  unauthenticated?: boolean;
  variant?: SupportRequestButtonVariant;
  label?: string;
  className?: string;
  title?: string;
}

const variantClassName: Record<SupportRequestButtonVariant, string> = {
  icon: "text-black03 hover:opacity-70",
  floating:
    "fixed bottom-6 right-6 z-40 h-12 w-12 rounded-full bg-primary text-white shadow-lg hover:opacity-90",
  text: "text-sm font-semibold text-black03 hover:opacity-70",
  sidebar:
    "h-11 w-52 justify-start gap-2 rounded-full px-6 py-2 font-semibold text-black04 hover:bg-white02",
};

const defaultLabel: Record<SupportRequestButtonVariant, string> = {
  icon: "Precisa de ajuda?",
  floating: "Precisa de ajuda?",
  text: "Precisa de ajuda?",
  sidebar: "Abrir chamado",
};

export const SupportRequestButton = ({
  context,
  unauthenticated = false,
  variant = "icon",
  label,
  className,
  title = "Abrir chamado",
}: SupportRequestButtonProps) => {
  const [open, setOpen] = useState(false);
  const { isSuperAdmin, isAdmin, isSupport } = useAuth();

  const isSupportStaff = isSuperAdmin || isAdmin || isSupport;
  const buttonLabel = label ?? defaultLabel[variant];
  const showsLabel = variant === "text" || variant === "sidebar";

  if (!unauthenticated && isSupportStaff) return null;

  return (
    <>
      <button
        type="button"
        title={showsLabel ? undefined : title}
        aria-label={showsLabel ? buttonLabel : title}
        onClick={() => setOpen(true)}
        className={cn(
          "flex items-center justify-center transition-opacity",
          variantClassName[variant],
          className,
        )}
      >
        {variant === "text" ? (
          buttonLabel
        ) : (
          <Headset className={variant === "sidebar" ? "" : "h-5 w-5"} />
        )}
        {variant === "sidebar" && <p>{buttonLabel}</p>}
      </button>

      <SupportRequestModal
        open={open}
        onOpenChange={setOpen}
        unauthenticated={unauthenticated}
        context={context}
      />
    </>
  );
};
